import React, { useState, useEffect } from "react";
import Select, { components } from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tab, Tabs } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import AsyncFilter from "./AsyncFilter";
import Input from "../../Builder/nodes/Logic/Inputs/Input";
import {
  useLazyGetInboundCampaignQuery,
  useLazyGetInboundCampaignsQuery,
} from "../../store/inboundCampaignSlice";
import {
  useLazyGetBroadcastQuery,
  useLazyGetBroadcastsQuery,
} from "../../store/slices/broadcastAPISlice";
import { useLazyGetProjectsQuery } from "../../store/slices/projectsAPISlice";
import { useLazyGetEventsQuery } from "../../store/slices/settingsSlice";

const StyledTabs = styled(Tabs)`
  border: none;
  font-weight: bold;
  .nav-item button {
    padding: 0 10px;
  }
`;

function parseMessageKinds(meta) {
  const supportedKinds = new Set([
    "inbound",
    "broadcast",
    "broadcaster",
    "project",
    "event",
    "email2sms",
    "email2voice",
    "process",
  ]);
  return (meta || "")
    .split(",")
    .map((item) => item.split(":"))
    .filter(([key, value]) => key === "kind" && supportedKinds.has(value))
    .map(([, value]) => value);
}

function parseReportMode(meta) {
  return (meta || "").split(",").includes("mode:api") ? "api" : "";
}

function parseMetaValues(meta) {
  return (meta || "").split(",").filter((item) => /^(process|workflow|project):[^:]+$/.test(item));
}

function CustomValueContainer({ children, ...props }) {
  const { getValue } = props;
  const nbValues = getValue().length;
  if (nbValues <= 2) {
    return (
      <components.ValueContainer {...props}>
        {children}
      </components.ValueContainer>
    );
  }
  return (
    <components.ValueContainer {...props}>
      <span
        style={{ whiteSpace: "nowrap" }}
        className="text-truncate"
      >{`${nbValues} selected`}</span>
      {/* {children[1]} */}
    </components.ValueContainer>
  );
}

// custom option for react select
// show a check sign next to the selected option
function CustomOption({ children, ...props }) {
  return props.isSelected ? (
    <components.Option {...props}>
      {children}
      <FontAwesomeIcon icon={faCheck} className="float-end" />
    </components.Option>
  ) : (
    <components.Option {...props}>{children}</components.Option>
  );
}

function Filters({
  submitFilters,
  colours,
  labels,
  loading,
  projectUUID,
  broadcastUUID,
  inboundUUID,
  eventUUID,
  startDate,
  endDate,
  comms,
  messageStatus,
  meta,
  hideFilters,
}) {
  // date/time manipulation for start date
  const calculatePeriodStart = (period) => {
    // subtract the period
    let newDate = new Date(new Date().setDate(new Date().getDate() - period));
    if (period === 1) {
      // if last 24 hours, get to the beginning of the hour
      newDate = new Date(newDate.setMinutes(0, 0, 0));
    } else {
      // otherwise get to the beginning of the day
      newDate = new Date(newDate.setHours(0, 0, 0, 0));
    }
    return newDate;
  };

  // select options
  const commOptions = ["sms", "email", "voice"];
  const statusOptions = [
    "delivered",
    "sent",
    "expired",
    "failed",
    "pending",
    "cancelled",
    "unknown",
  ];
  const periodOptions = [
    { value: 1, label: `Last 24 hours` },
    { value: 7, label: "Last 7 days" },
    { value: 30, label: "Last 30 days" },
    { value: "billing-0", label: "Current month" },
    { value: "billing-1", label: "Last month" },
    { value: "billing-2", label: "2 months back" },
    { value: "billing-3", label: "3 months back" },
  ];
  const timeUnits = [
    { value: "hour", label: "Hours" },
    { value: "day", label: "Days" },
    { value: "week", label: "Weeks" },
    { value: "month", label: "Months" },
  ];
  const unitOptions = [
    { value: "billable", label: "Billable" },
    { value: "transactions", label: "Transactions" },
  ];
  const showWhenOptions = [
    { value: "received", label: "Received" },
    { value: "processed", label: "Processed" },
  ];

  // get the URL parameters
  // const [sp] = useSearchParams();
  // const { projectUUID, broadcastUUID, inboundUUID, startDate, endDate } =
  //   Object.fromEntries(sp.entries());

  // default values selected in filters
  const defaultFilters = {
    startDate: startDate ? new Date(startDate) : calculatePeriodStart(1),
    endDate: endDate
      ? new Date(endDate)
      : new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate() + 1
        ),
    billing: null,
    showWhen: "received",
    comms: comms ? comms.split(",").filter((channel) => commOptions.includes(channel)) : ["sms", "email", "voice"],
    messageDirection: ["1-Way", "2-Way"],
    messageStatus: messageStatus
      ? messageStatus.split(",").filter((status) => statusOptions.includes(status))
      : statusOptions,
    recipientQueryString: "",
    timeUnit: timeUnits[1].value,
    customers: [],
    accounts: [],
    unit: "transactions",
    projectUUID,
    broadcastUUID,
    inboundUUID,
    eventUUID,
    metaKind: parseMessageKinds(meta),
    mode: parseReportMode(meta),
    metaValues: parseMetaValues(meta),
  };

  // state
  const [period, setPeriod] = useState(periodOptions[0]);
  const [filters, setFilters] = useState(defaultFilters);

  // submit the form on first page load
  useEffect(() => {
    submitFilters(filters);
  }, []);

  useEffect(() => {
    setFilters((previous) => {
      const newFilters = {
        ...previous,
        projectUUID,
        broadcastUUID,
        inboundUUID,
        eventUUID,
        startDate: startDate ? new Date(startDate) : previous.startDate,
        comms: comms
          ? comms.split(",").filter((channel) => commOptions.includes(channel))
          : previous.comms,
        messageStatus: messageStatus
          ? messageStatus.split(",").filter((status) => statusOptions.includes(status))
          : previous.messageStatus,
        metaKind: parseMessageKinds(meta),
        mode: parseReportMode(meta),
        metaValues: parseMetaValues(meta),
      };
      submitFilters(newFilters);
      return newFilters;
    });
  }, [projectUUID, broadcastUUID, inboundUUID, eventUUID, startDate, endDate, comms, messageStatus, meta]);

  // colour styling for the components
  const colourStyles = {
    control: (styles) => ({ ...styles, backgroundColor: "white" }),
    option: (styles, { data, isDisabled }) => {
      const color = colours[data.value];
      return {
        ...styles,
        color,
        cursor: isDisabled ? "not-allowed" : "default",
        backgroundColor: "white",
        ":hover": {
          backgroundColor: "#eee",
        },
      };
    },

    multiValue: (styles, { data }) => {
      const color = colours[data.value] || "grey";
      return {
        ...styles,
        color: "white",
        backgroundColor: color,
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: "white",
    }),
  };

  // if time period is more than a week disable group by hours
  const checkGroupByOptionDisabled = (option) => {
    const timediff =
      (filters.endDate - filters.startDate) / 1000 / 60 / 60 / 24;
    if (option.value === "hour" && timediff > 8) {
      // if group by hours is already selected, unselect it
      if (filters.timeUnit === "hour") {
        setTimeout(() => {
          setFilters((previous) => ({
            ...previous,
            timeUnit: "day",
          }));
        }, 100);
      }
      return true;
    }
    return false;
  };

  const errors = (
    <>
      {filters.messageStatus.length === 0 && (
        <p className="text-white text-center fw-bold mt-2">
          You must select at least one message status
        </p>
      )}
      {filters.comms.length === 0 && (
        <p className="text-white text-center fw-bold mt-2">
          You must select at least one communication channel
        </p>
      )}
      {filters.messageDirection.length === 0 && (
        <p className="text-white text-center fw-bold mt-2">
          You must select at least one message direction
        </p>
      )}
    </>
  );

  const [selectedTab, setSelectedTab] = useState(
    startDate ? "range" : "period"
  );

  const [getInbounds] = useLazyGetInboundCampaignsQuery();
  const [getInbound] = useLazyGetInboundCampaignQuery();
  const [getBroadcasts] = useLazyGetBroadcastsQuery();
  const [getBroadcast] = useLazyGetBroadcastQuery();
  const [getEvents] = useLazyGetEventsQuery();

  const [getProjects] = useLazyGetProjectsQuery();

  if (hideFilters) {
    return (
      <div className="bg-colored-reverse p-3 rounded-3 my-3">
        <div className="d-flex align-items-end justify-content-between gap-3">
          <div className="col-lg-4 col-sm-6">
            <div>
              <div className="filter-header fw-bold text-white">
                Message Status
              </div>
              <Select
                placeholder="Select one option"
                hasValue
                classNamePrefix="react-select"
                isMulti
                options={statusOptions.map((item) => ({
                  value: item,
                  label: labels[item],
                }))}
                value={filters.messageStatus.map((item) => ({
                  value: item,
                  label: labels[item],
                }))}
                closeMenuOnSelect={false}
                onChange={(options) =>
                  setFilters((previous) => ({
                    ...previous,
                    messageStatus: options.map((item) => item.value),
                  }))
                }
                styles={colourStyles}
                components={{
                  ValueContainer: CustomValueContainer,
                  Option: CustomOption,
                }}
                hideSelectedOptions={false}
              />
            </div>
          </div>

          <div className="col-lg-3">
            <button
              type="button"
              onClick={() => submitFilters({ ...filters, refreshed: Date() })}
              disabled={
                filters.messageStatus.length === 0 ||
                filters.endDate === null ||
                filters.comms.length === 0 ||
                (filters.comms.includes("sms") &&
                  filters.messageDirection.length === 0) ||
                loading.report ||
                loading.table
              }
              className="bg-black text-white border-none rounded-2 p-2 px-4 border-0 fw-bold w-100"
            >
              Generate Report
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-colored-reverse p-3 rounded-3 my-3">
      <div className="d-flex align-items-center gap-3">
        <div className="flex-grow-1">
          {" "}
          {/* First row */}
          <div className="row">
            <div
              className={`${
                selectedTab === "period" ? "col-lg-2" : "col-lg-4"
              } col-sm-6`}
            >
              <StyledTabs
                variant="tabs"
                activeKey={selectedTab}
                id="date-tab"
                animation={false}
                onSelect={(key) => setSelectedTab(key)}
                className="flex-nowrap text-truncate"
              >
                <Tab eventKey="period" title="Period">
                  <Select
                    classNamePrefix="react-select"
                    value={
                      period || {
                        label: `${filters.startDate.toLocaleString()} - ${filters.endDate.toLocaleString()}`,
                      }
                    }
                    options={periodOptions}
                    onChange={(option) => {
                      if (
                        [
                          "billing-0",
                          "billing-1",
                          "billing-2",
                          "billing-3",
                        ].includes(option.value)
                      ) {
                        setFilters((previous) => ({
                          ...previous,
                          billing: option.value,
                        }));
                        setPeriod(option);
                        return;
                      }
                      const d = new Date();
                      setFilters((previous) => ({
                        ...previous,
                        billing: null,
                        startDate: calculatePeriodStart(option.value),
                        endDate: new Date(
                          d.getFullYear(),
                          d.getMonth(),
                          d.getDate() + 1
                        ),
                      }));
                      setPeriod(option);
                    }}
                    styles={colourStyles}
                    components={{
                      Option: CustomOption,
                    }}
                  />
                </Tab>
                <Tab eventKey="range" title="Date range">
                  <div style={{ display: "flex", gap: 6 }}>
                    <DatePicker
                      className="form-control"
                      showTimeSelect
                      selected={filters.startDate}
                      placeholderText="Click to select start date/time"
                      dateFormat="dd/MM/yyyy HH:mm"
                      minDate={
                        new Date(new Date().setMonth(new Date().getMonth() - 3))
                      }
                      maxDate={filters.endDate}
                      onChange={(date, e) => {
                        if (!e && date >= filters.endDate) {
                          toast.error(
                            "Start date needs to be earlier than end date"
                          );
                          setFilters((previous) => ({
                            ...previous,
                            billing: null,
                            startDate: filters.endDate,
                          }));
                          setPeriod(null);
                          return;
                        }
                        setFilters((previous) => ({
                          ...previous,
                          billing: null,
                          startDate: date,
                        }));
                        setPeriod(null);
                      }}
                    />
                    <DatePicker
                      className="form-control"
                      showTimeSelect
                      selected={filters.endDate}
                      placeholderText="Click to select end date/time"
                      dateFormat="dd/MM/yyyy HH:mm"
                      minDate={filters.startDate}
                      maxDate={new Date()}
                      onChange={(date, e) => {
                        if (!e && date < filters.startDate) {
                          toast.error(
                            "End date needs to be later than starter date"
                          );
                          setFilters((previous) => ({
                            ...previous,
                            billing: null,
                            endDate: filters.startDate,
                          }));
                          setPeriod(null);
                          return;
                        }
                        setFilters((previous) => ({
                          ...previous,
                          billing: null,
                          endDate: date,
                        }));
                        setPeriod(null);
                      }}
                    >
                      <div className="text-center margin-10">
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => {
                            const d = new Date();
                            setFilters((previous) => ({
                              ...previous,
                              billing: null,
                              endDate: new Date(
                                d.getFullYear(),
                                d.getMonth(),
                                d.getDate() + 1
                              ),
                            }));
                            setPeriod(null);
                          }}
                        >
                          end of today
                        </button>
                      </div>
                    </DatePicker>
                  </div>
                </Tab>
              </StyledTabs>
            </div>
            <div className="col-lg-2 col-sm-6">
              <div>
                <div className="filter-header fw-bold text-white">
                  Show when
                </div>
                <Select
                  placeholder="Select one option"
                  classNamePrefix="react-select"
                  isMulti={false}
                  options={showWhenOptions}
                  value={showWhenOptions.find(
                    (s) => s.value === filters.showWhen
                  )}
                  onChange={(option) =>
                    setFilters((previous) => ({
                      ...previous,
                      showWhen: option.value,
                    }))
                  }
                  closeMenuOnSelect
                  styles={colourStyles}
                  components={{
                    Option: CustomOption,
                  }}
                />
              </div>
            </div>
            <div className="col-lg-2 col-sm-6">
              <div>
                <div className="filter-header fw-bold text-white">Unit</div>
                <Select
                  placeholder="Select one option"
                  classNamePrefix="react-select"
                  options={unitOptions}
                  value={unitOptions.find((opt) => opt.value === filters.unit)}
                  closeMenuOnSelect={false}
                  onChange={(option) =>
                    setFilters((previous) => ({
                      ...previous,
                      unit: option.value,
                    }))
                  }
                  styles={colourStyles}
                  hideSelectedOptions={false}
                  components={{
                    ValueContainer: CustomValueContainer,
                    Option: CustomOption,
                  }}
                />
              </div>
            </div>
            <div className="col-lg-2 col-sm-6">
              <div>
                <div className="filter-header fw-bold text-white">Group by</div>
                <Select
                  placeholder="Select one option"
                  classNamePrefix="react-select"
                  isMulti={false}
                  options={timeUnits}
                  isOptionDisabled={(option) =>
                    checkGroupByOptionDisabled(option)
                  }
                  value={timeUnits.find(
                    (timeUnit) => timeUnit.value === filters.timeUnit
                  )}
                  onChange={(option) =>
                    setFilters((previous) => ({
                      ...previous,
                      timeUnit: option.value,
                    }))
                  }
                  closeMenuOnSelect
                  styles={colourStyles}
                  components={{
                    Option: CustomOption,
                  }}
                />
              </div>
            </div>
            <div className="col-lg-2 col-sm-6">
              <div>
                <div className="filter-header fw-bold text-white">
                  Message Status
                </div>
                <Select
                  placeholder="Select one option"
                  hasValue
                  classNamePrefix="react-select"
                  isMulti
                  options={statusOptions.map((item) => ({
                    value: item,
                    label: labels[item],
                  }))}
                  value={filters.messageStatus.map((item) => ({
                    value: item,
                    label: labels[item],
                  }))}
                  closeMenuOnSelect={false}
                  onChange={(options) =>
                    setFilters((previous) => ({
                      ...previous,
                      messageStatus: options.map((item) => item.value),
                    }))
                  }
                  styles={colourStyles}
                  components={{
                    ValueContainer: CustomValueContainer,
                    Option: CustomOption,
                  }}
                  hideSelectedOptions={false}
                />
              </div>
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-lg-2 col-sm-6">
              <div>
                <div className="filter-header fw-bold text-white">Channel</div>
                <Select
                  placeholder="Select one option"
                  classNamePrefix="react-select"
                  isMulti
                  options={commOptions.map((item) => ({
                    value: item,
                    label: labels[item],
                  }))}
                  value={filters.comms.map((item) => ({
                    value: item,
                    label: labels[item],
                  }))}
                  onChange={(options) => {
                    setFilters((previous) => ({
                      ...previous,
                      comms: options.map((option) => option.value),
                    }));
                  }}
                  closeMenuOnSelect={false}
                  styles={colourStyles}
                  components={{
                    ValueContainer: CustomValueContainer,
                    Option: CustomOption,
                  }}
                  hideSelectedOptions={false}
                />
              </div>
            </div>
            {filters.comms.includes("sms") && (
              <div className="col-lg-2 col-sm-6">
                <div className="filter-header fw-bold text-white">
                  SMS Direction
                </div>
                <Input
                  placeholder="Select one option"
                  isDisabled={!filters.comms.includes("sms")}
                  isMulti
                  inputType="select"
                  options={[
                    {
                      value: "1-Way",
                      label: "1-Way",
                    },
                    {
                      value: "2-Way",
                      label: " 2-Way",
                    },
                  ]}
                  // formatOptionLabel={(o) => (
                  //   <span
                  //     className="bg-colored-reverse text-white p-1 fw-bold rounded-2"
                  //   >
                  //     {o.label}
                  //   </span>
                  // )}
                  value={filters.messageDirection}
                  // closeMenuOnSelect={false}
                  onChange={(options) =>
                    setFilters((previous) => ({
                      ...previous,
                      messageDirection: options,
                    }))
                  }
                  // styles={colourStyles}
                  // hideSelectedOptions={false}
                  // components={{
                  //   ValueContainer: CustomValueContainer,
                  //   Option: CustomOption,
                  // }}
                />
              </div>
            )}

            {!projectUUID && !broadcastUUID && !inboundUUID && (
              <>
                <div className="col-lg-2 col-sm-6">
                  <div className="filter-header fw-bold text-white">
                    Message kind
                  </div>
                  <Input
                    placeholder="Select one option"
                    inputType="select"
                    isMulti
                    options={[
                      { value: "inbound", label: "Inbound" },
                      { value: "broadcast", label: "Broadcast" },
                      { value: "broadcaster", label: "Broadcast template" },
                      // { value: "workflow", label: "Workflow" },
                      { value: "project", label: "Project" },
                      { value: "event", label: "Event" },
                      { value: "email2sms", label: "Email-to-SMS" },
                      { value: "email2voice", label: "Email-to-Voice" },
                      // { value: "handler", label: "Handler" },
                    ]}
                    value={filters.metaKind}
                    onChange={(metaKind) =>
                      setFilters((previous) => ({
                        ...previous,
                        metaKind: metaKind || [],
                        inboundUUID: null,
                        broadcastUUID: null,
                        projectUUID: null,
                        eventUUID: null,
                      }))
                    }
                  />
                </div>
                {filters.metaKind.length === 1 && filters.metaKind[0] === "inbound" && (
                  <div className="col-lg-2 col-sm-6">
                    <div className="filter-header fw-bold text-white">
                      Inbound campaigns
                    </div>
                    <Input
                      placeholder="Select one option"
                      inputType="select"
                      isClearable
                      // options={[]}
                      loadOptions={async (search, prevOptions) => {
                        const {
                          data: { items, total },
                        } = await getInbounds({ search });
                        return {
                          options: items.map((item) => ({
                            value: item.Uuid,
                            label: `${item.Label}`,
                            data: item,
                          })),
                          hasMore: items.length < total,
                        };
                      }}
                      // formatOptionLabel={(o) => (
                      //   <span>
                      //     {o.data.Label}{" "}
                      //     <span className="text-secondary">
                      //       {o.data.Description}
                      //     </span>
                      //   </span>
                      // )}
                      // getValueLabels={async (vs) => {
                      //   const { data } = await getInbound(vs[0] || "");
                      //   return [{ value: data.Uuid, label: data.Label }];
                      // }}
                      value={filters.inboundUUID ? [filters.inboundUUID] : []}
                      onChange={([o]) => {
                        setFilters((previous) => ({
                          ...previous,
                          inboundUUID: o,
                        }));
                      }}
                    />
                  </div>
                )}

                {filters.metaKind.length === 1 && filters.metaKind[0] === "broadcaster" && (
                  <div className="col-lg-2 col-sm-6">
                    <div className="filter-header fw-bold text-white">
                      Broadcast template
                    </div>
                    <Input
                      placeholder="Select one option"
                      inputType="select"
                      isClearable
                      // options={[]}
                      loadOptions={async (search, prevOptions) => {
                        const {
                          data: { data: items, total },
                        } = await getBroadcasts({ search });
                        return {
                          options: items.map((item) => ({
                            value: item.Uuid,
                            label: `${item.Label}`,
                            data: item,
                          })),
                          hasMore: items.length < total,
                        };
                      }}
                      // formatOptionLabel={(o) => (
                      //   <span>
                      //     {o.data.Label}{" "}
                      //     <span className="text-secondary">
                      //       {o.data.Description}
                      //     </span>{" "}
                      //   </span>
                      // )}
                      // getValueLabels={async (vs) => {
                      //   const { data } = await getBroadcast(vs[0] || "");
                      //   return [{ value: data.Uuid, label: data.Label }];
                      // }}
                      value={
                        filters.broadcastUUID ? [filters.broadcastUUID] : []
                      }
                      onChange={([o]) =>
                        setFilters((previous) => ({
                          ...previous,
                          broadcastUUID: o,
                        }))
                      }
                    />
                  </div>
                )}

                {filters.metaKind.length === 1 && filters.metaKind[0] === "event" && (
                  <div className="col-lg-2 col-sm-6">
                    <div className="filter-header fw-bold text-white">
                      Event
                    </div>
                    <Input
                      placeholder="Select one option"
                      inputType="select"
                      isClearable
                      // options={[]}
                      loadOptions={async (search, prevOptions) => {
                        const {
                          data: { events: items, total },
                        } = await getEvents({ search });
                        return {
                          options: items.map((item) => ({
                            value: item.Uuid,
                            label: `${item.Label}`,
                            data: item,
                          })),
                          hasMore: items.length < total,
                        };
                      }}
                      // formatOptionLabel={(o) => (
                      //   <span>
                      //     {o.data.Label}{" "}
                      //     <span className="text-secondary">
                      //       {o.data.Description}
                      //     </span>{" "}
                      //   </span>
                      // )}
                      // getValueLabels={async (vs) => {
                      //   const { data } = await getBroadcast(vs[0] || "");
                      //   return [{ value: data.Uuid, label: data.Label }];
                      // }}
                      value={filters.eventUUID ? [filters.eventUUID] : []}
                      onChange={([o]) =>
                        setFilters((previous) => ({
                          ...previous,
                          eventUUID: o,
                        }))
                      }
                    />
                  </div>
                )}

                {filters.metaKind.length === 1 && filters.metaKind[0] === "project" && (
                  <div className="col-lg-2 col-sm-6">
                    <div className="filter-header fw-bold text-white">
                      Project
                    </div>
                    <Input
                      placeholder="Select one option"
                      inputType="select"
                      isClearable
                      // options={[]}
                      loadOptions={async (search, prevOptions) => {
                        const {
                          data: { projects: items, total },
                        } = await getProjects({ label: search });
                        return {
                          options: items.map((item) => ({
                            value: item.UUID,
                            label: `${item.Label} `,
                            data: item,
                          })),
                          hasMore: items.length < total,
                        };
                      }}
                      // formatOptionLabel={(o) => (
                      //   <span>
                      //     {o.data.Label}{" "}
                      //     <span className="text-secondary">
                      //       {o.data.Description}
                      //     </span>{" "}
                      //   </span>
                      // )}
                      value={filters.projectUUID ? [filters.projectUUID] : []}
                      onChange={([o]) =>
                        setFilters((previous) => ({
                          ...previous,
                          projectUUID: o,
                        }))
                      }
                    />
                  </div>
                )}
              </>
            )}
            <div className="col-lg-2 col-sm-6">
              <div>
                <div className="filter-header fw-bold text-white">
                  Recipient
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="starts with 447..."
                  value={filters.recipientQueryString}
                  onChange={(e) =>
                    setFilters((previous) => ({
                      ...previous,
                      recipientQueryString: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            {document.querySelector("#app").dataset.hasCustomers && (
              <div className="col-lg-2 col-sm-6">
                <div>
                  <div className="filter-header fw-bold text-white">
                    Customers
                  </div>
                  <AsyncFilter
                    value={filters.customers}
                    onChange={(opts) =>
                      setFilters((previous) => ({
                        ...previous,
                        customers: opts,
                        // filter the selected accounts
                        accounts: previous.accounts.filter((acc) =>
                          opts.map((opt) => opt.val).includes(acc.customer_id)
                        ),
                      }))
                    }
                    searchURL="/reports/customers-autocomplete?search="
                    CustomOption={CustomOption}
                    colourStyles={colourStyles}
                    CustomValueContainer={CustomValueContainer}
                  />
                </div>
              </div>
            )}
            <div className="col-lg-2 col-sm-6">
              <div>
                <div className="filter-header fw-bold text-white">Accounts</div>
                <AsyncFilter
                  value={filters.accounts}
                  onChange={(value) =>
                    setFilters((previous) => ({
                      ...previous,
                      accounts: value,
                    }))
                  }
                  searchURL={`/reports/accounts-autocomplete?customers=${filters.customers.map(
                    (c) => c.value
                  )}&search=`}
                  CustomOption={CustomOption}
                  colourStyles={colourStyles}
                  CustomValueContainer={CustomValueContainer}
                />
              </div>
            </div>
          </div>{" "}
          {errors}
        </div>

        <div className="">
          <div className="d-flex align-items-bottom h-100">
            <div style={{ display: "flex" }}>
              <button
                type="button"
                onClick={() => submitFilters({ ...filters, refreshed: Date() })}
                disabled={
                  filters.messageStatus.length === 0 ||
                  filters.endDate === null ||
                  filters.comms.length === 0 ||
                  (filters.comms.includes("sms") &&
                    filters.messageDirection.length === 0) ||
                  loading.report ||
                  loading.table
                }
                className="bg-black text-white border-none rounded-4 py-3 px-4 border-0 fw-bold"
                style={{ flexGrow: 1 }}
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
}

export default Filters;
